import { z } from 'zod';
import { FINANCIAL_ENTRY_STATUS } from '../constants';
import {
  FIELD_ERROR_PLACEHOLDER,
  isInstallmentComplete,
  isInstallmentStarted,
  isItemFieldComplete,
  isItemFieldStarted,
  INSTALLMENT_INCOMPLETE_MESSAGE,
} from './formValidation';
import { parseCurrencyValue } from '../financialFormatters';

const requiredId = (message: string) =>
  z
    .number({ invalid_type_error: message })
    .nullable()
    .refine((value): value is number => value !== null && value > 0, message);

const requiredOption = (message: string) =>
  z
    .number({ invalid_type_error: message })
    .nullable()
    .refine((value): value is number => value !== null, message);

const installmentSchema = z.object({
  id: z.number().optional(),
  value: z.string().optional(),
  dueDate: z.string().optional(),
  commission: z.string().optional(),
  hasCommission: z.boolean().optional(),
  installmentNumber: z.string().optional(),
});

const itemSchema = z.object({
  id: z.number().optional(),
  businessUnit: z.string().optional(),
  project: z.string().optional(),
  competenceValue: z.string().optional(),
  competenceDate: z.string().optional(),
  attachments: z.array(z.any()).optional(),
  pendingFiles: z.array(z.any()).optional(),
});

export const financialEntryFormSchema = z
  .object({
    companyId: requiredId('Selecione a empresa'),
    companyName: z.string().optional(),
    linkedUserId: requiredId('Selecione o usuário vinculado'),
    linkedUserName: z.string().optional(),
    type: requiredOption('Selecione o tipo'),
    category: requiredOption('Selecione a categoria'),
    status: requiredOption('Selecione o status'),
    recurrence: z.number().nullable().optional(),
    description: z.string().optional(),
    paymentMethodId: requiredOption('Selecione a forma de pagamento'),
    freight: z.string().optional(),
    icms: z.string().optional(),
    ipi: z.string().optional(),
    installments: z.array(installmentSchema).min(1, 'Informe ao menos uma parcela'),
    items: z.array(itemSchema).optional(),
  })
  .superRefine((data, ctx) => {
    const installments = data.installments ?? [];
    const completeInstallments = installments.filter(isInstallmentComplete);

    if (completeInstallments.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: INSTALLMENT_INCOMPLETE_MESSAGE,
        path: ['installments'],
      });

      const targetIndex = installments.findIndex(isInstallmentStarted);
      const indexToMark = targetIndex >= 0 ? targetIndex : 0;
      const installment = installments[indexToMark];

      if (!installment?.value?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FIELD_ERROR_PLACEHOLDER,
          path: ['installments', indexToMark, 'value'],
        });
      }

      if (!installment?.dueDate?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FIELD_ERROR_PLACEHOLDER,
          path: ['installments', indexToMark, 'dueDate'],
        });
      }
    }

    installments.forEach((installment, index) => {
      if (!isInstallmentStarted(installment) || isInstallmentComplete(installment)) {
        return;
      }

      if (!installment.value?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FIELD_ERROR_PLACEHOLDER,
          path: ['installments', index, 'value'],
        });
      }

      if (!installment.dueDate?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FIELD_ERROR_PLACEHOLDER,
          path: ['installments', index, 'dueDate'],
        });
      }
    });

    (data.items ?? []).forEach((item, index) => {
      if (!isItemFieldStarted(item) || isItemFieldComplete(item)) {
        return;
      }

      if (!item.businessUnit?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FIELD_ERROR_PLACEHOLDER,
          path: ['items', index, 'businessUnit'],
        });
      }

      if (!item.project?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FIELD_ERROR_PLACEHOLDER,
          path: ['items', index, 'project'],
        });
      }

      if (parseCurrencyValue(item.competenceValue) === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FIELD_ERROR_PLACEHOLDER,
          path: ['items', index, 'competenceValue'],
        });
      }

      if (!item.competenceDate?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FIELD_ERROR_PLACEHOLDER,
          path: ['items', index, 'competenceDate'],
        });
      }
    });
  });

export type FinancialEntryFormSchemaType = z.input<typeof financialEntryFormSchema>;

export type FinancialEntryFormInstallment =
  FinancialEntryFormSchemaType['installments'][number];

export type FinancialEntryFormItem = NonNullable<
  FinancialEntryFormSchemaType['items']
>[number];

export const defaultFormSchemaValues: FinancialEntryFormSchemaType = {
  companyId: null,
  companyName: '',
  linkedUserId: null,
  linkedUserName: '',
  type: null,
  category: null,
  status: FINANCIAL_ENTRY_STATUS.BUDGETED,
  recurrence: null,
  description: '',
  paymentMethodId: null,
  freight: '',
  icms: '',
  ipi: '',
  installments: [
    {
      value: '',
      dueDate: '',
      commission: '',
      hasCommission: false,
      installmentNumber: '1/1',
    },
  ],
  items: [
    {
      businessUnit: '',
      project: '',
      competenceValue: '',
      competenceDate: '',
      attachments: [],
      pendingFiles: [],
    },
  ],
};
